import React, { useState } from "react";
import { FaCopy, FaMapMarkerAlt, FaRegClock } from "react-icons/fa";
import { GiPathDistance } from "react-icons/gi";
import { BsCalendarEvent } from "react-icons/bs";
import { toast } from "react-toastify";
import SupportTicketModal from "../SupportTicketModal";

import axios from "axios";
import { useSelector } from "react-redux";
import { API_URL } from "../../config/apiConfig";

export default function TripDetailsCard({
  booking,
  rawBookingData,
}) {
  const [copiedOTP, setCopiedOTP] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [hasExistingMessage, setHasExistingMessage] = useState(false);
  const [checkingMessage, setCheckingMessage] = useState(true);
  const [showViaCities, setShowViaCities] = useState(false);
  const { user: userInfo } = useSelector((state) => state.auth);
  const corporateToken = localStorage.getItem("corporateToken");
  const isCorporateSession = !!corporateToken && !(userInfo && userInfo.token);

  // Check if user already submitted a message for this booking
  React.useEffect(() => {
    if (rawBookingData?._id) {
      // For regular users with token or corporate sessions with token
      if (userInfo?.token || isCorporateSession) {
        checkExistingMessage();
      }
      // For non-logged-in users, don't check
      else {
        setCheckingMessage(false);
      }
    } else {
      setCheckingMessage(false);
    }
  }, [rawBookingData?._id, userInfo?.token, isCorporateSession]);

  // Re-check after message submission
  React.useEffect(() => {
    if (showSupportModal === false && rawBookingData?._id && (userInfo?.token || isCorporateSession)) {
      checkExistingMessage();
    }
  }, [showSupportModal, isCorporateSession, userInfo?.token]);

  const checkExistingMessage = async () => {
    try {
      setCheckingMessage(true);

      // Use corporate token if available, otherwise use user token
      const token = isCorporateSession ? corporateToken : userInfo?.token;

      if (!token) {
        setCheckingMessage(false);
        return;
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.get(
        `${API_URL}/support-message/my-messages?page=1&limit=100`,
        config
      );

      if (response.data.success) {
        // Check if there's already a message for this booking
        const existingMessage = response.data.data.some(
          (msg) =>
            msg.bookingId === rawBookingData?._id ||
            msg.bookingId?._id === rawBookingData?._id
        );
        setHasExistingMessage(existingMessage);
      }
    } catch (error) {
      console.error('Error checking existing message:', error);
    } finally {
      setCheckingMessage(false);
    }
  };

  const handleCopyOTP = () => {
    if (booking?.startOtp) {
      navigator.clipboard.writeText(booking.startOtp);
      setCopiedOTP(true);
      toast.success("OTP copied to clipboard!");
      setTimeout(() => setCopiedOTP(false), 2000);
    }
  };

  // Helper function to format time to HH:MM AM/PM
  const formatTimeDisplay = (timeStr) => {
    if (!timeStr) return "Not set";
    if (typeof timeStr === "string") {
      // If it's already in HH:MM format, try to convert to 12-hour
      const match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
      if (match) {
        const hours = parseInt(match[1]);
        const minutes = match[2];
        const ampm = hours >= 12 ? "pm" : "am";
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${minutes} ${ampm}`;
      }
      return timeStr;
    }
    return "Not set";
  };

  // Format distance with commas
  const formatDistanceDisplay = (distance) => {
    if (!distance) return "0 kms";
    const num = parseFloat(distance);
    return isNaN(num) ? "0 kms" : `${num.toLocaleString()} kms`;
  };

  return (
    <div className="w-full lg:max-w-[320px] bg-gradient-to-br from-white to-slate-50/50 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.03)] border border-slate-100/80 p-5 flex flex-col mb-4 lg:mb-0 lg:h-full lg:overflow-y-auto lg:overflow-x-hidden [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-300 transition-all duration-300 ease-out">
      {/* Title */}
      <h2 className="text-lg font-semibold text-gray-900 mb-2">
        Trip Details
      </h2>

      {/* OTP Section - Hidden when trip is completed, in progress, or cancelled */}
      {booking?.startOtp &&
        !["completed", "inprogress", "in_progress", "cancelled"].includes(booking?.status?.toLowerCase()) &&
        !["completed", "inprogress", "in_progress", "cancelled"].includes(rawBookingData?.status?.toLowerCase()) && (
        <div className="bg-gray-100 rounded-lg p-3 mb-3">
          <p className="text-sm font-semibold text-gray-700 mb-2">
            Trip Start OTP
          </p>

          <div className="bg-white rounded-md p-3 flex items-center justify-between shadow-sm">
            <div className="text-center flex-1">
              <p className="text-2xl font-bold text-gray-900 tracking-widest">
                {booking.startOtp}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Share this OTP with Driver
              </p>
            </div>

            <button
              onClick={handleCopyOTP}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs px-3 py-1 rounded-md flex items-center gap-1 ml-2 transition"
            >
              <FaCopy size={12} />
              Copy
            </button>
          </div>
        </div>
      )}

      {/* Route Info */}
      <div className="bg-blue-50 rounded-lg p-3 mb-3 flex-1">
        <p className="text-sm font-semibold text-gray-700 mb-3">
          Route Information
        </p>

        {/* Locations */}
        <div className="mb-3 flex flex-col">
          {/* Pickup Location */}
          <div className="flex gap-3 min-w-0">
            <div className="flex flex-col items-center flex-shrink-0 w-3 self-stretch">
              <span className="w-3 h-3 rounded-full bg-green-500 mt-1"></span>
              <div className="w-0.5 flex-1 bg-gray-300 mt-1"></div>
            </div>
            <div className="flex-1 min-w-0 pb-4">
              <p className="font-semibold text-gray-900 text-sm truncate" title={booking?.pickupAddressCity || booking?.from || "Pickup Location"}>
                {booking?.pickupAddressCity || booking?.from || "Pickup Location"}
              </p>
              <p className="text-xs text-gray-500 mt-0.5 truncate" title={booking?.pickupAddress || "Address details"}>
                {booking?.pickupAddress || "Address details"}
              </p>
            </div>
          </div>

          {/* Via Cities Dropdown Toggle */}
          {booking?.viaCities && booking.viaCities.length > 0 && (
            <div className="flex gap-3 items-stretch min-w-0">
              {/* Timeline Connector Line */}
              <div className="flex flex-col items-center flex-shrink-0 w-3 self-stretch">
                <div className="w-0 flex-1 border-l-2 border-dashed border-yellow-400"></div>
              </div>
              
              {/* Toggle Content Card */}
              <div className="flex-1 min-w-0 py-1.5">
                <button
                  type="button"
                  onClick={() => setShowViaCities(!showViaCities)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-gradient-to-r from-yellow-50 to-orange-50 hover:from-yellow-100 hover:to-orange-100 border border-yellow-200/80 rounded-xl shadow-xs transition-all duration-200 focus:outline-none cursor-pointer group min-w-0"
                >
                  <span className="flex items-center gap-2 text-xs font-semibold text-yellow-800 truncate">
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-yellow-500 text-white shadow-sm transition-transform duration-200 group-hover:scale-110 shrink-0">
                      📍
                    </span>
                    <span className="truncate">{booking.viaCities.length} Intermediate Stop{booking.viaCities.length === 1 ? "" : "s"}</span>
                  </span>
                  
                  <span className="flex items-center gap-1 text-[11px] font-bold text-yellow-700 uppercase tracking-wider bg-white/80 group-hover:bg-white px-2 py-0.5 rounded-lg border border-yellow-200/40 shrink-0 ml-2">
                    {showViaCities ? "Hide" : "View"}
                    <span className="text-[9px]">{showViaCities ? "▲" : "▼"}</span>
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Via Cities Expanded Content */}
          {booking?.viaCities && booking.viaCities.length > 0 && showViaCities && (
            <div className="flex flex-col min-w-0">
              {booking.viaCities.map((city, idx) => {
                const cityName = typeof city === "string" ? city : (city?.name || city?.address || `Stop ${idx + 1}`);
                const isObj = typeof city === "object" && city !== null;
                const subAddress = isObj && city?.address && city.address !== city.name ? city.address : `Stop ${idx + 1} • Intermediate Stop`;

                return (
                  <div className="flex gap-3 min-w-0" key={idx}>
                    {/* Timeline Stop Icon & Line */}
                    <div className="flex flex-col items-center flex-shrink-0 w-3 self-stretch">
                      <span className="w-2.5 h-2.5 rounded-full bg-yellow-500 shadow-sm border border-white mt-1.5 flex-shrink-0"></span>
                      <div className="w-0 flex-1 border-l-2 border-dashed border-yellow-400 mt-1"></div>
                    </div>
                    {/* Stop Details */}
                    <div className="flex-1 min-w-0 pb-3">
                      <div className="bg-yellow-50/60 rounded-xl p-2.5 border border-yellow-200/70 min-w-0 shadow-xs">
                        <p className="font-semibold text-gray-800 text-[13px] truncate block" title={cityName}>
                          {cityName}
                        </p>
                        <p className="text-[11px] text-gray-500 mt-0.5 truncate block" title={subAddress}>
                          {subAddress}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Drop Location */}
          <div className="flex gap-3 min-w-0">
            <div className="flex flex-col items-center flex-shrink-0 w-3 mt-1">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 text-sm truncate" title={booking?.dropAddressCity || booking?.to || "Drop-off Location"}>
                {booking?.dropAddressCity || booking?.to || "Drop-off Location"}
              </p>
              <p className="text-xs text-gray-500 mt-0.5 truncate" title={booking?.dropAddress || "Address details"}>
                {booking?.dropAddress || "Address details"}
              </p>
            </div>
          </div>
        </div>

        {/* Trip Meta */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-gray-600 mb-3 py-2 border-t border-blue-100">
          <div className="flex items-center gap-2">
            <GiPathDistance className="text-blue-600" />
            <span>{formatDistanceDisplay(booking?.distance)}</span>
          </div>
          <div className="flex items-center gap-2">
            <BsCalendarEvent className="text-blue-600" />
            <span>{booking?.date || "Date not set"}</span>
          </div>
          <div className="flex items-center gap-2">
            <FaRegClock className="text-blue-600" />
            <span>{formatTimeDisplay(booking?.pickupTime)}</span>
          </div>
          <div className="flex items-center gap-2">
            <span>⏳</span>
            <span>{booking?.duration || "Duration N/A"}</span>
          </div>
        </div>

        {/* Car Card */}
        {(rawBookingData?.cabType || booking?.cabType) && (
          <div className="border border-blue-400 rounded-lg p-2 bg-white flex items-center gap-3">
            {/* Left side - Image with fuel tag */}
            <div className="flex flex-col items-center gap-1">
              <div className="w-20 h-14 bg-gray-50 rounded flex items-center justify-center overflow-hidden">
                {rawBookingData?.cabType?.imageUrl ? (
                  <img
                    src={rawBookingData.cabType.imageUrl}
                    alt={rawBookingData.cabType.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                  />
                ) : null}
                <span className="text-2xl" style={{ display: rawBookingData?.cabType?.imageUrl ? 'none' : 'flex' }}>🚗</span>
              </div>
              <span className="text-xs text-orange-500 font-medium bg-orange-50 px-2 py-0.5 rounded">
                {rawBookingData?.cabType?.fuelType ? `${rawBookingData.cabType.fuelType.charAt(0).toUpperCase() + rawBookingData.cabType.fuelType.slice(1)}` : "Petrol"}
              </span>
            </div>

            {/* Right side - Details */}
            <div className="flex-1">
              <p className="font-semibold text-gray-900 text-sm">
                {rawBookingData?.cabType?.name || booking?.cabType || "Standard Cab"}
              </p>
              <p className="text-xs text-gray-500">
                {rawBookingData?.cabType?.description || "exact model"}
              </p>
              <p className="text-xs text-gray-700 mt-1">
                {rawBookingData?.cabType?.seatingCapacity || "6"} Seats | {rawBookingData?.cabType?.acType || "AC"}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Selected Add-on Services (Optional) */}
      {((rawBookingData?.selectedAddOns && rawBookingData.selectedAddOns.length > 0) || 
        (booking?.selectedAddOns && booking.selectedAddOns.length > 0)) && (
        <div className="bg-amber-50/80 border border-amber-200/80 rounded-xl p-3 my-3">
          <div className="text-xs font-bold text-amber-900 flex items-center gap-1.5 mb-2">
            <span>✨</span> Add-on Services (Optional)
          </div>
          <div className="space-y-1.5">
            {(rawBookingData?.selectedAddOns || booking?.selectedAddOns || []).map((addon, index) => (
              <div key={index} className="flex justify-between items-center text-xs">
                <span className="text-gray-700 font-medium">• {addon.name}</span>
                <span className="font-bold text-amber-950">+₹{addon.price}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Support Button or Already Submitted Message */}
      {rawBookingData?.status?.toLowerCase() === 'pending' ? (
        <div className="w-full mt-auto bg-gray-50 border border-gray-300 rounded-lg p-3 text-center">
          <p className="text-sm font-medium text-gray-800">
            ⏳ Contact Support Unavailable
          </p>
          <p className="text-xs text-gray-700 mt-1">
            Contact Support will be available after your trip is completed.
          </p>
        </div>
      ) : hasExistingMessage ? (
        <div className="w-full mt-auto bg-green-50 border border-green-300 rounded-lg p-3 text-center">
          <p className="text-sm font-medium text-green-800">
            ✓ Support Ticket Submitted
          </p>
          <p className="text-xs text-green-700 mt-1">
            You can only submit one support ticket per booking. The admin will review and respond to your message.
          </p>
        </div>
      ) : (
        <button
          onClick={() => setShowSupportModal(true)}
          disabled={checkingMessage}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-1.5 rounded-lg font-medium transition mt-auto text-sm"
        >
          {checkingMessage ? 'Loading...' : 'Contact Support'}
        </button>
      )}

      {/* Support Modal */}
      <SupportTicketModal
        isOpen={showSupportModal}
        onClose={() => {
          setShowSupportModal(false);
          // Re-check for existing messages after modal closes
          setTimeout(() => checkExistingMessage(), 300);
        }}
        bookingId={rawBookingData?._id}
        bookingNumber={booking?.id}
        onSubmitSuccess={() => {
          // Immediately check for existing messages after successful submission
          checkExistingMessage();
        }}
      />
    </div>
  );
}
